package com.arena_web.data.websocket

import com.arena_web.core.config.AppConfig
import com.arena_web.core.logger.getLogger
import com.arena_web.data.dto.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.dom.WebSocket
import org.w3c.dom.events.Event

sealed class ChatWsEvent {
    data class NewMessage(val message: MessageDto) : ChatWsEvent()
    data class MessageDeleted(val data: MessageDeletedData) : ChatWsEvent()
    data class MessageEdited(val data: MessageEditedData) : ChatWsEvent()
    data class UserTyping(val data: UserTypingData) : ChatWsEvent()
    data class UserOnline(val data: UserOnlineStatusData) : ChatWsEvent()
    data class UserOffline(val data: UserOnlineStatusData) : ChatWsEvent()
    data class ReactionAdded(val data: ReactionChangedData) : ChatWsEvent()
    data class ReactionRemoved(val data: ReactionChangedData) : ChatWsEvent()
    data class MemberJoined(val data: ChatMemberDto) : ChatWsEvent()
    data class MemberLeft(val userId: String) : ChatWsEvent()
    data class MemberKicked(val data: ChatMemberKickedData) : ChatWsEvent()
    data object Connected : ChatWsEvent()
    data object Disconnected : ChatWsEvent()
}

class ChatWebSocketClient {
    private val logger = getLogger<ChatWebSocketClient>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    private var webSocket: WebSocket? = null
    private var currentChatId: String? = null
    private var reconnectAttempts = 0
    private var reconnectTimerId: Int? = null
    private var intentionalDisconnect = false

    private val _events = MutableSharedFlow<ChatWsEvent>(extraBufferCapacity = 64)
    val events: SharedFlow<ChatWsEvent> = _events.asSharedFlow()

    fun connect(chatId: String) {
        disconnect()
        intentionalDisconnect = false
        currentChatId = chatId

        val token = Storage.get(StorageKeys.ACCESS_TOKEN) ?: run {
            logger.error("No access token for WebSocket connection")
            return
        }

        val url = "${AppConfig.wsUrl}/chats/$chatId?token=$token"
        logger.debug("WS connecting to $url")

        try {
            val ws = WebSocket(url)

            ws.onopen = {
                logger.debug("WS connected to chat $chatId")
                reconnectAttempts = 0
                _events.tryEmit(ChatWsEvent.Connected)
                Unit
            }

            ws.onmessage = { event: Event ->
                val msgEvent = event.asDynamic()
                val text = msgEvent.data as? String
                if (text != null) {
                    handleMessage(text)
                }
                Unit
            }

            ws.onclose = {
                logger.debug("WS disconnected from chat $chatId")
                _events.tryEmit(ChatWsEvent.Disconnected)
                if (!intentionalDisconnect) {
                    scheduleReconnect()
                }
                Unit
            }

            ws.onerror = {
                logger.error("WS error on chat $chatId")
                Unit
            }

            webSocket = ws
        } catch (e: Exception) {
            logger.error("Failed to create WebSocket: ${e.message}")
        }
    }

    fun disconnect() {
        intentionalDisconnect = true
        reconnectTimerId?.let { kotlinx.browser.window.clearTimeout(it) }
        reconnectTimerId = null
        reconnectAttempts = 0
        // Detach handlers BEFORE close. The browser fires onclose
        // asynchronously, and by the time it runs `intentionalDisconnect` may
        // have been flipped back to false by a follow-up connect(), which
        // would trick scheduleReconnect() into firing for a socket we never
        // wanted to keep. Nulling the handlers severs that lifecycle entirely.
        webSocket?.let { ws ->
            ws.onopen = null
            ws.onmessage = null
            ws.onerror = null
            ws.onclose = null
            ws.close()
        }
        webSocket = null
        currentChatId = null
    }

    private fun scheduleReconnect() {
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            logger.error("Chat WS max reconnect attempts reached")
            return
        }
        val chatId = currentChatId ?: return
        val delayMs = RECONNECT_DELAYS.getOrElse(reconnectAttempts) { RECONNECT_DELAYS.last() }
        reconnectAttempts++
        logger.debug("Chat WS reconnecting in ${delayMs}ms (attempt $reconnectAttempts)")
        reconnectTimerId = kotlinx.browser.window.setTimeout({
            reconnectTimerId = null
            if (!intentionalDisconnect) {
                connect(chatId)
            }
        }, delayMs)
    }

    fun sendTyping() {
        try {
            webSocket?.send("""{"type":"TYPING"}""")
        } catch (e: Exception) {
            logger.error("Failed to send typing: ${e.message}")
        }
    }

    private fun handleMessage(text: String) {
        try {
            val jsonElement = json.parseToJsonElement(text)
            val obj = jsonElement.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return
            val data = obj["data"] ?: return

            val event = when (type) {
                "CHAT_MESSAGE" -> ChatWsEvent.NewMessage(
                    json.decodeFromJsonElement(MessageDto.serializer(), data)
                )
                "MESSAGE_DELETED" -> ChatWsEvent.MessageDeleted(
                    json.decodeFromJsonElement(MessageDeletedData.serializer(), data)
                )
                "MESSAGE_EDITED" -> ChatWsEvent.MessageEdited(
                    json.decodeFromJsonElement(MessageEditedData.serializer(), data)
                )
                "USER_TYPING" -> ChatWsEvent.UserTyping(
                    json.decodeFromJsonElement(UserTypingData.serializer(), data)
                )
                "USER_ONLINE" -> ChatWsEvent.UserOnline(
                    json.decodeFromJsonElement(UserOnlineStatusData.serializer(), data)
                )
                "USER_OFFLINE" -> ChatWsEvent.UserOffline(
                    json.decodeFromJsonElement(UserOnlineStatusData.serializer(), data)
                )
                "MESSAGE_REACTION_ADDED" -> ChatWsEvent.ReactionAdded(
                    json.decodeFromJsonElement(ReactionChangedData.serializer(), data)
                )
                "MESSAGE_REACTION_REMOVED" -> ChatWsEvent.ReactionRemoved(
                    json.decodeFromJsonElement(ReactionChangedData.serializer(), data)
                )
                "CHAT_MEMBER_JOINED" -> ChatWsEvent.MemberJoined(
                    json.decodeFromJsonElement(ChatMemberDto.serializer(), data)
                )
                "CHAT_MEMBER_LEFT" -> {
                    val userId = extractUserId(data)
                    if (userId != null) ChatWsEvent.MemberLeft(userId) else null
                }
                "CHAT_MEMBER_KICKED" -> ChatWsEvent.MemberKicked(
                    json.decodeFromJsonElement(ChatMemberKickedData.serializer(), data)
                )
                else -> {
                    logger.debug("Unknown WS event type: $type")
                    null
                }
            }

            if (event != null) {
                _events.tryEmit(event)
            }
        } catch (e: Exception) {
            logger.error("Failed to parse WS message: ${e.message}")
        }
    }

    private fun extractUserId(data: JsonElement): String? {
        return try {
            data.jsonObject["userId"]?.jsonPrimitive?.content
        } catch (_: Exception) {
            null
        }
    }

    companion object {
        private const val MAX_RECONNECT_ATTEMPTS = 5
        private val RECONNECT_DELAYS = listOf(1000, 2000, 3000, 3000, 3000)
    }
}
